import { useEffect, useRef, useState } from "react"

import {
  acquireDocumentLockApi,
  type DocumentLockInfo,
  releaseDocumentLockApi,
  releaseDocumentLockOnUnload,
} from "@/api/client/documents"
import { SettingsService } from "@/services/settings-service"

const HEARTBEAT_MS = 45_000

export type ArtifactLockStatus = "idle" | "acquiring" | "held" | "locked-by-other"

export type ArtifactLockState = {
  status: ArtifactLockStatus
  lockedBy?: DocumentLockInfo
}

/**
 * Check-out edit lock for a document-backed artifact. While `enabled`, acquires
 * the lock, renews it on a heartbeat, and releases it on unmount (plus a
 * best-effort keepalive-fetch release on page hide, so a hard tab close frees
 * the lock immediately instead of waiting out the server TTL). If another user
 * holds it, returns status "locked-by-other" with the holder so the UI can go
 * read-only.
 */
export function useArtifactLock(documentId: string | null, enabled: boolean): ArtifactLockState {
  const [state, setState] = useState<ArtifactLockState>({ status: "idle" })
  const heldRef = useRef(false)

  useEffect(() => {
    if (!documentId || !enabled) {
      setState({ status: "idle" })
      return
    }

    let cancelled = false
    const token = SettingsService.getInstance().token

    const tryAcquire = async () => {
      try {
        const { status, lock } = await acquireDocumentLockApi(documentId, token)
        if (cancelled) return
        if (status === 200) {
          heldRef.current = true
          setState({ status: "held" })
        } else if (status === 409) {
          heldRef.current = false
          setState({ status: "locked-by-other", lockedBy: lock })
        }
      } catch {
        // Network error → leave prior state; heartbeat will retry.
      }
    }

    setState({ status: "acquiring" })
    void tryAcquire()
    const timer = setInterval(() => void tryAcquire(), HEARTBEAT_MS)

    // A hard tab close / navigation may not run the React cleanup below, so
    // release via keepalive fetch on pagehide too. Skip when the page is going
    // into the bfcache (persisted) — it may be restored and the heartbeat keeps
    // renewing. Double-release is guarded by heldRef.
    const onPageHide = (event: PageTransitionEvent) => {
      if (event.persisted || !heldRef.current) return
      heldRef.current = false
      releaseDocumentLockOnUnload(documentId, token)
    }
    window.addEventListener("pagehide", onPageHide)

    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener("pagehide", onPageHide)
      // A closed tab that can't release still frees within the lock TTL (~2 min).
      if (heldRef.current) {
        heldRef.current = false
        void releaseDocumentLockApi(documentId, token)
      }
    }
  }, [documentId, enabled])

  return state
}
